import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Link } from "react-router-dom";

/* ------------------ HARDCODED DATA ------------------ */
const pageData = {
  hero: {
    badge: "Fuel Your Performance",
    title: "Nutrition System",
  },
  methodology: {
    description:
      "A simple, sustainable, and performance-driven nutrition system.",
    pillars: [
      {
        title: "Macro Tracking",
        description:
          "Understand your calories and macros to fuel your body optimally.",
      },
      {
        title: "Whole Foods",
        description:
          "Focus on nutrient-dense foods for better performance and recovery.",
      },
      {
        title: "Smart Supplements",
        description: "Use supplements strategically to fill nutritional gaps.",
      },
    ],
  },
  calculator: {
    title: "Calculate Your Macros",
    description: "Get personalized macro recommendations based on your goals",
  },
  meals_section: { title: "Sample Meal Plan" },
  sample_meals: [
    {
      name: "Breakfast Bowl",
      description: "High protein start to your day",
      calories: 450,
      protein: 35,
      carbs: 40,
      fat: 15,
      items: ["Oats", "Whey Protein", "Banana", "Peanut Butter"],
    },
    {
      name: "Grilled Chicken Lunch",
      description: "Balanced muscle-building meal",
      calories: 600,
      protein: 50,
      carbs: 55,
      fat: 20,
      items: ["Chicken Breast", "Rice", "Broccoli", "Olive Oil"],
    },
    {
      name: "Evening Snack",
      description: "Light and healthy snack",
      calories: 250,
      protein: 20,
      carbs: 20,
      fat: 10,
      items: ["Greek Yogurt", "Almonds", "Honey"],
    },
  ],
  supplements_section: { title: "Recommended Supplements" },
  supplements: [
    {
      name: "Whey Protein",
      timing: "Post Workout",
      benefit: "Supports muscle recovery and growth",
    },
    {
      name: "Creatine",
      timing: "Anytime",
      benefit: "Improves strength and performance",
    },
    {
      name: "Fish Oil",
      timing: "With Meals",
      benefit: "Supports heart and joint health",
    },
    {
      name: "Multivitamin",
      timing: "Morning",
      benefit: "Covers micronutrient gaps",
    },
  ],
  cta: {
    title: "Get Your Personalized Plan",
    description:
      "DVX Pro and Elite members receive fully customized nutrition plans tailored to their goals",
    button_text: "View Plans",
    button_href: "/pricing",
  },
};

/* ------------------ HELPERS ------------------ */
const fmt = (n) => Math.round(n).toLocaleString();

const GOAL_LABELS = {
  lose: "Fat Loss",
  gain: "Muscle Gain",
  maintain: "Maintenance",
  recomp: "Recomp",
};

const ACTIVITY_MULTIPLIERS = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  veryActive: 1.9,
};

/* ------------------ MACRO CALCULATOR ------------------ */
const MacroCalculator = () => {
  const [formData, setFormData] = useState({
    weight: "",
    height: "",
    age: "",
    gender: "male",
    activity: "moderate",
    goal: "lose",
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  const calculateMacros = () => {
    const { weight, height, age, gender, activity, goal } = formData;

    if (!weight || !height || !age) {
      setError("Please enter age, weight, and height.");
      return;
    }
    setError("");

    const w = parseFloat(weight);
    const h = parseFloat(height);
    const a = parseFloat(age);

    // BMR via Mifflin-St Jeor
    const bmr =
      gender === "male"
        ? 10 * w + 6.25 * h - 5 * a + 5
        : 10 * w + 6.25 * h - 5 * a - 161;

    // TDEE via activity multiplier
    const tdee = bmr * (ACTIVITY_MULTIPLIERS[activity] ?? 1.55);

    // Goal-adjusted calories + macro multipliers
    let calories = tdee;
    let proteinMultiplier = 1.8;
    let fatMultiplier = 0.8;

    if (goal === "lose") {
      calories = tdee - 450;
      proteinMultiplier = 2.2;
      fatMultiplier = 0.8;
    } else if (goal === "gain") {
      calories = tdee + 300;
      proteinMultiplier = 2.0;
      fatMultiplier = 0.8;
    } else if (goal === "maintain") {
      calories = tdee;
      proteinMultiplier = 1.8;
      fatMultiplier = 0.9;
    } else if (goal === "recomp") {
      calories = tdee - 200;
      proteinMultiplier = 2.2;
      fatMultiplier = 0.8;
    }

    const protein = w * proteinMultiplier;
    const fats = w * fatMultiplier;

    const proteinCals = protein * 4;
    const fatCals = fats * 9;
    const remainingCalories = calories - (proteinCals + fatCals);
    const carbs = Math.max(0, remainingCalories / 4);
    const carbCals = carbs * 4;

    setResults({
      bmr,
      tdee,
      calories,
      protein,
      fats,
      carbs,
      proteinCals,
      fatCals,
      carbCals,
    });
  };

  const reset = () => {
    setFormData({
      weight: "",
      height: "",
      age: "",
      gender: "male",
      activity: "moderate",
      goal: "lose",
    });
    setResults(null);
    setError("");
  };

  const totalCals = results?.calories || 1;
  const proteinPct = results
    ? Math.max(8, Math.min(100, (results.proteinCals / totalCals) * 100))
    : 0;
  const carbsPct = results
    ? Math.max(8, Math.min(100, (results.carbCals / totalCals) * 100))
    : 0;
  const fatsPct = results
    ? Math.max(8, Math.min(100, (results.fatCals / totalCals) * 100))
    : 0;

  return (
    /* Outer two-column layout: form left, results right */
    <div className="flex flex-col lg:flex-row gap-6 items-start">
      {/* ── LEFT: Form ── */}
      <div className="w-full lg:w-1/2 bg-[var(--dvx-surface)] border border-[var(--dvx-border)] p-6 lg:p-8">
        <h3 className="font-oswald text-2xl font-bold text-[var(--dvx-text)] uppercase mb-6">
          Macro Calculator
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Numeric inputs */}
          {[
            { field: "age", label: "Age", placeholder: "e.g. 28" },
            { field: "weight", label: "Weight (kg)", placeholder: "e.g. 84" },
            { field: "height", label: "Height (cm)", placeholder: "e.g. 188" },
          ].map(({ field, label, placeholder }) => (
            <div key={field}>
              <Label className="text-[var(--dvx-text-muted)] mb-2 block">
                {label}
              </Label>
              <Input
                type="number"
                placeholder={placeholder}
                value={formData[field]}
                onChange={(e) =>
                  setFormData({ ...formData, [field]: e.target.value })
                }
                className="bg-[var(--dvx-bg)] border-[var(--dvx-border)] text-[var(--dvx-text)] h-12"
              />
            </div>
          ))}

          {/* Gender */}
          <div>
            <Label className="text-[var(--dvx-text-muted)] mb-2 block">
              Gender
            </Label>
            <Select
              value={formData.gender}
              onValueChange={(v) => setFormData({ ...formData, gender: v })}
            >
              <SelectTrigger className="bg-[var(--dvx-bg)] border-[var(--dvx-border)] text-[var(--dvx-text)] h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Activity — full width */}
          <div className="sm:col-span-2">
            <Label className="text-[var(--dvx-text-muted)] mb-2 block">
              Activity Level
            </Label>
            <Select
              value={formData.activity}
              onValueChange={(v) => setFormData({ ...formData, activity: v })}
            >
              <SelectTrigger className="bg-[var(--dvx-bg)] border-[var(--dvx-border)] text-[var(--dvx-text)] h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sedentary">
                  Sedentary — little or no exercise
                </SelectItem>
                <SelectItem value="light">
                  Light — training 1–3 days/week
                </SelectItem>
                <SelectItem value="moderate">
                  Moderate — training 3–5 days/week
                </SelectItem>
                <SelectItem value="active">
                  Heavy — training 6–7 days/week
                </SelectItem>
                <SelectItem value="veryActive">
                  Athlete — twice daily / very high output
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Goal — full width */}
          <div className="sm:col-span-2">
            <Label className="text-[var(--dvx-text-muted)] mb-2 block">
              Goal
            </Label>
            <Select
              value={formData.goal}
              onValueChange={(v) => setFormData({ ...formData, goal: v })}
            >
              <SelectTrigger className="bg-[var(--dvx-bg)] border-[var(--dvx-border)] text-[var(--dvx-text)] h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lose">Lose Fat</SelectItem>
                <SelectItem value="gain">Gain Muscle</SelectItem>
                <SelectItem value="maintain">Maintain</SelectItem>
                <SelectItem value="recomp">Recomp</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {error && <p className="mt-4 text-red-400 text-sm">{error}</p>}

        <div className="flex gap-3 mt-6">
          <Button onClick={calculateMacros} className="flex-1 py-4">
            Calculate Macros
          </Button>
          <Button
            onClick={reset}
            variant="outline"
            className="px-6 py-4 border-[var(--dvx-border)] text-[var(--dvx-text)]"
          >
            Reset
          </Button>
        </div>

        <p className="mt-4 text-xs text-[var(--dvx-text-muted)] leading-relaxed">
          Formula: BMR via Mifflin-St Jeor → TDEE via activity multiplier →
          goal-adjusted calories → protein &amp; fat set by bodyweight → carbs
          fill remaining calories.
        </p>
      </div>

      {/* ── RIGHT: Results panel — always visible, dimmed until calculated ── */}
      <div
        className="w-full lg:w-1/2 lg:sticky lg:top-6 border border-[var(--dvx-border)] p-6 lg:p-8 transition-opacity duration-300"
        style={{
          background: "var(--dvx-surface)",
          opacity: results ? 1 : 0.45,
          pointerEvents: results ? "auto" : "none",
          userSelect: results ? "auto" : "none",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-oswald text-2xl font-bold text-[var(--dvx-text)] uppercase">
            Your Results
          </h3>
          <span
            className="text-xs font-bold uppercase tracking-widest px-3 py-1 border rounded-full"
            style={{
              borderColor: "var(--dvx-primary)",
              color: "var(--dvx-primary)",
              background: "rgba(var(--dvx-primary-rgb, 255,122,0), 0.1)",
            }}
          >
            {GOAL_LABELS[formData.goal]}
          </span>
        </div>

        {/* Daily Calories */}
        <div
          className="p-5 border border-[var(--dvx-border)] mb-5"
          style={{ background: "var(--dvx-bg)" }}
        >
          <p className="text-xs text-[var(--dvx-text-muted)] uppercase tracking-widest mb-1">
            Daily Calories
          </p>
          <p className="text-4xl font-black tracking-tight text-[var(--dvx-text)]">
            {results ? fmt(results.calories) : "—"}{" "}
            <span className="text-lg font-normal text-[var(--dvx-text-muted)]">
              kcal
            </span>
          </p>
        </div>

        {/* Macro bars */}
        <div className="space-y-4 mb-5">
          {[
            {
              label: "Protein",
              grams: results?.protein,
              cals: results?.proteinCals,
              pct: proteinPct,
            },
            {
              label: "Carbs",
              grams: results?.carbs,
              cals: results?.carbCals,
              pct: carbsPct,
            },
            {
              label: "Fats",
              grams: results?.fats,
              cals: results?.fatCals,
              pct: fatsPct,
            },
          ].map(({ label, grams, cals, pct }) => (
            <div
              key={label}
              className="p-4 border border-[var(--dvx-border)]"
              style={{ background: "var(--dvx-bg)" }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-[var(--dvx-text)]">
                  {label}
                </span>
                <span className="text-xl font-black text-[var(--dvx-text)]">
                  {grams != null ? `${Math.round(grams)}g` : "—"}
                </span>
              </div>
              <div
                className="w-full h-2 rounded-full overflow-hidden mb-1"
                style={{ background: "var(--dvx-border)" }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${pct}%`,
                    background: "var(--dvx-primary)",
                    transition: "width 0.4s ease",
                  }}
                />
              </div>
              <p className="text-xs text-[var(--dvx-text-muted)]">
                {cals != null ? `${fmt(cals)} kcal` : "—"}
              </p>
            </div>
          ))}
        </div>

        {/* BMR / TDEE breakdown */}
        <div
          className="p-4 border border-dashed border-[var(--dvx-border)] text-sm leading-7 text-[var(--dvx-text)] mb-5"
          style={{ background: "var(--dvx-bg)" }}
        >
          <strong>BMR:</strong> {results ? `${fmt(results.bmr)} kcal/day` : "—"}
          <br />
          <strong>TDEE:</strong>{" "}
          {results ? `${fmt(results.tdee)} kcal/day` : "—"}
          <br />
          <strong>Goal:</strong> {GOAL_LABELS[formData.goal]}
          <br />
          <strong>Macro Split:</strong>{" "}
          {results
            ? `${Math.round(results.protein)}g protein, ${Math.round(results.carbs)}g carbs, ${Math.round(results.fats)}g fats`
            : "—"}
        </div>

        {/* 4-cell summary */}
        <div className="grid grid-cols-2 gap-3 text-center">
          {[
            { key: "Calories", val: results ? fmt(results.calories) : "—" },
            {
              key: "Protein",
              val: results ? `${Math.round(results.protein)}g` : "—",
            },
            {
              key: "Carbs",
              val: results ? `${Math.round(results.carbs)}g` : "—",
            },
            {
              key: "Fats",
              val: results ? `${Math.round(results.fats)}g` : "—",
            },
          ].map(({ key, val }) => (
            <div
              key={key}
              className="p-4 border border-[var(--dvx-border)]"
              style={{ background: "var(--dvx-bg)" }}
            >
              <div className="text-xl font-bold text-[var(--dvx-text)]">
                {val}
              </div>
              <div className="text-xs uppercase text-[var(--dvx-text-muted)] mt-1">
                {key}
              </div>
            </div>
          ))}
        </div>

        {!results && (
          <p className="mt-5 text-center text-sm text-[var(--dvx-text-muted)]">
            Fill in your details and hit <strong>Calculate Macros</strong> to
            see your personalised DVX targets.
          </p>
        )}
      </div>
    </div>
  );
};

/* ------------------ MAIN PAGE ------------------ */
export default function Nutrition() {
  return (
    <div className="min-h-screen bg-[var(--dvx-bg)] pt-24">
      {/* HERO */}
      <section className="py-20 text-center">
        <span className="text-[var(--dvx-primary)] uppercase">
          {pageData.hero.badge}
        </span>
        <h1 className="text-5xl font-bold">{pageData.hero.title}</h1>
        <p className="mt-4 text-gray-400">{pageData.methodology.description}</p>
      </section>

      {/* CALCULATOR */}
      <section className="py-16 bg-[var(--dvx-surface)]">
        <div className="max-w-6xl mx-auto px-4">
          <MacroCalculator />
        </div>
      </section>

      {/* MEALS */}
      {/* <section className="py-16">
        <h2 className="text-center text-3xl mb-10">
          {pageData.meals_section.title}
        </h2>
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {pageData.sample_meals.map((meal, i) => (
            <div key={i} className="border p-6">
              <h3 className="font-bold">{meal.name}</h3>
              <p>{meal.description}</p>
            </div>
          ))}
        </div>
      </section> */}

      {/* CTA */}
      <section className="py-20 text-center">
        <h2 className="text-3xl">{pageData.cta.title}</h2>
        <Link to="/nutrition-plans">
          <Button className="mt-4">
            {pageData.cta.button_text} <ArrowRight />
          </Button>
        </Link>
      </section>
    </div>
  );
}
